/**
 * Feature engagement analytics: typed event vocabulary plus a hook that
 * records `feature_engagement` custom RUM events. Recording is synchronous,
 * non-throwing, and gated on shortbread performance-cookie consent read at
 * call time. The clickstream path is never referenced; events go exclusively
 * through the RUM client.
 */
import { useCallback } from 'react';
import { useCookieConsent } from '@/src/store/cookie-consent.context';
import { getAwsRum } from '../awsrum.utils';
import { preProdLogger } from '../log.utils';
import type { AWSCShortbread } from '@amzn/shortbread';

/** Interaction types. Exactly five members, no more. */
export const FEATURE_ACTIONS = ['view', 'click', 'enable', 'disable', 'submit'] as const;
export type FeatureAction = (typeof FEATURE_ACTIONS)[number];

/** Allowed feature identifiers. Unique, non-empty string values. */
export enum FeatureName {
  LEADERBOARD_PRESENTER_MODE = 'leaderboard_presenter_mode',
  AI_ASSISTANT = 'ai_assistant',
  AI_EVENT_CREATION = 'ai_event_creation',
  VIRTUAL_MC = 'virtual_mc',
  AI_TEAMMATE = 'ai_teammate',
}

/** Where a Virtual MC toggle was changed from. */
export enum VirtualMcSource {
  EVENT_CREATION = 'event creation',
  ADVANCED_SETTINGS = 'advanced settings',
}

/**
 * Allowed values for the optional `source` metadata attribute. Restricted to a
 * union of enums so recorded metadata can only ever be a known constant — user
 * data cannot be attached to an event even by accident. Extend this union when
 * a new feature needs its own source values.
 */
export type FeatureSource = VirtualMcSource;

/** RUM custom event type recorded for every feature interaction. */
export const FEATURE_ENGAGEMENT_EVENT_TYPE = 'feature_engagement';

/** Optional metadata. Values are constrained to the enums above. */
export interface FeatureMetadata {
  source?: FeatureSource;
  messageId?: string;
  actionCategory?: string;
}

/**
 * Payload passed to `awsRum.recordEvent`. `feature` and `action` are the
 * dimensions extracted into CloudWatch metrics.
 */
export interface FeatureEngagementEventPayload extends FeatureMetadata {
  feature: FeatureName;
  action: FeatureAction;
}

/** Reasons a tracking call may be rejected. The tracker never throws. */
export type TrackRejectReason =
  | 'invalid_feature' // missing/empty feature id
  | 'invalid_action' // action not a member of the vocabulary
  | 'consent_denied' // performance cookie consent not granted
  | 'rum_unavailable' // RUM singleton not initialized
  | 'record_error'; // recordEvent threw; suppressed

/** Result the caller can inspect; the tracker NEVER throws. */
export type TrackResult = { recorded: true } | { recorded: false; reason: TrackRejectReason };

export type TrackFeatureUsage = (
  feature: FeatureName,
  action: FeatureAction,
  metadata?: FeatureMetadata
) => TrackResult;

const isValidFeature = (feature: unknown): feature is string =>
  typeof feature === 'string' && feature.trim().length > 0;

const isValidAction = (action: unknown): action is FeatureAction =>
  typeof action === 'string' && (FEATURE_ACTIONS as readonly string[]).includes(action);

const record = (
  feature: FeatureName,
  action: FeatureAction,
  metadata: FeatureMetadata | undefined,
  consentGranted: boolean
): TrackResult => {
  if (!isValidFeature(feature)) {
    return { recorded: false, reason: 'invalid_feature' };
  }
  if (!isValidAction(action)) {
    return { recorded: false, reason: 'invalid_action' };
  }
  if (!consentGranted) {
    return { recorded: false, reason: 'consent_denied' };
  }

  const awsRum = getAwsRum();
  if (!awsRum) {
    preProdLogger('[engagement-tracker] RUM client unavailable; skipping feature_engagement event', {
      feature,
      action,
    });
    return { recorded: false, reason: 'rum_unavailable' };
  }

  const payload: FeatureEngagementEventPayload = { feature, action, ...metadata };

  // Suppress any error so tracking never disrupts the caller.
  try {
    awsRum.recordEvent(FEATURE_ENGAGEMENT_EVENT_TYPE, payload);
    return { recorded: true };
  } catch (error) {
    preProdLogger('[engagement-tracker] recordEvent threw; suppressing', { feature, action, error });
    return { recorded: false, reason: 'record_error' };
  }
};

/**
 * Returns a function that records exactly one `feature_engagement` RUM event
 * per call. Consent is read from shortbread's consent cookie at call time, so
 * a withdrawal takes effect immediately. Invalid input, missing consent, or an
 * unavailable RUM client yield a rejected `TrackResult` instead of an error.
 */
export const useFeatureUsageTracker = (): TrackFeatureUsage => {
  const { shortbread } = useCookieConsent();

  return useCallback(
    (feature, action, metadata) => record(feature, action, metadata, isPerformanceConsentGranted(shortbread)),
    [shortbread]
  );
};

const isPerformanceConsentGranted = (shortbread: AWSCShortbread | undefined): boolean =>
  shortbread?.getConsentCookie()?.performance === true;
